<template>
    <div>
        <building-form  
            :is-building-form-sidebar-active.sync="isBuildingFormSidebarActive"
            :buildings-data="buildingsData" 
            :langs-data="langs"
            @refetch-data="refetchData"  
        />
        <floor-plan-form
            :is-floorplan-form-sidebar-active.sync="isFloorplanFormSidebarActive"
            :building="buildingItem" 
            :langs-data="langs"
            @refetch-data="refetchData"
        />
        <b-card no-body>
            <div class="m-2">
                <div class="d-flex align-items-center justify-content-between">
                    <h1>{{ (housePin && housePin.name && housePin.name[currentLocale]) }}</h1>
                    <b-button
                        variant="primary"
                        @click="addBuilding('')"
                    >
                        <span class="text-nowrap">Add Building</span>
                    </b-button>
                </div>
            </div>
        </b-card>

        <draggable
            v-model="buildings"
            handle=".handle"
            @end="changeOrderBuilding"
        >
            <b-card-actions  
                v-for="(item, index) in buildings" 
                :key="'repeat-' + index"  
                :handle="true" 
                :title="item.title ? item.title[currentLocale] : ''"
                action-collapse
            >
                <div class="m-2">
                    <div class="d-flex justify-content-between mb-3">
                        <div>
                            <b-button 
                                v-ripple.400="'rgba(0, 207, 232, 0.15)'" 
                                variant="outline-info" 
                                @click="addFloorPlan(item)"
                            >Add Floor Plan</b-button>
                        </div>
                    </div>
                    
                    <div class="row">
                        <draggable
                            v-model="item.floorplans"
                            handle=".handle"
                            @end="(e) => updateFloorplanOrdering(e, item)"
                        >
                                <div
                                    v-for="(itm, idx) in item.floorplans"
                                    :key="itm.id || idx"
                                    class="col-12"
                                >
                                    <div class="draggable-item d-flex align-items-center p-2"
                                        style="min-height: 100px;"
                                    >
                                        <MenuIcon icon="MenuIcon" class="handle mr-2" />
                                        <b-img :src="itm.image" fluid alt="Fluid image" width="80" height="50" class="mr-2"></b-img>
                                        <span class="mr-2">{{ itm.title ? itm.title[currentLocale] : '' }}</span>
                                        <b-button 
                                        variant="danger" 
                                        size="sm" 
                                        @click="deleteFloorplan(itm)"
                                        >
                                        Delete
                                        </b-button>
                                    </div>
                                </div>
                        </draggable>
                    </div>
                </div>
                <div class="d-flex justify-content-end mt-3">
                    <b-button
                        variant="outline-primary" 
                        size="sm"
                        class="button-edit"
                        @click="addBuilding(item)"
                    >
                        edit
                    </b-button>
                    <b-button
                        variant="outline-danger" 
                        size="sm"
                        @click="confirmRemoveBuilding(item, index)"
                    >
                        delete
                    </b-button>
                </div>
            </b-card-actions>
        </draggable>
    </div>
</template>

<script>
import {
    BCard, BRow, BCol, BFormInput, BButton, BTable, BMedia, BAvatar, BLink,
    BBadge, BDropdown, BDropdownItem, BPagination, BTooltip,VBModal,BFormGroup,BFormCheckbox,
    BListGroupItem,BCardHeader,BCardBody, BTab, BTabs, BImg,
} from 'bootstrap-vue'
import { onUnmounted } from '@vue/composition-api'
import {  MenuIcon } from 'vue-feather-icons'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import Ripple from 'vue-ripple-directive'
import store from '@/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import draggable from 'vuedraggable'
import Prism from 'vue-prism-component'
import 'prismjs'
import 'prismjs/themes/prism-tomorrow.css'
import useHousePinFloorPlan from './useHousePinFloorPlan'
import BuildingForm from './BuildingForm.vue'
import FloorPlanForm from './FloorPlanForm.vue'
import languagesStoreModule from '../../languages/languagesStoreModule.js'
import housePinStoreModule from '../housePinStoreModule'

export default {
    components:{
        BCard, BRow, BCol, BFormInput, BButton, BTable, BMedia, BAvatar, BLink,
        BBadge, BDropdown, BDropdownItem, BPagination, BTooltip, VBModal, BFormGroup, BFormCheckbox,
        BListGroupItem, BCardHeader, BCardBody, BCardActions, draggable, Prism,
        BuildingForm, MenuIcon, BCardActions, FloorPlanForm, BTab, BTabs, BImg,
    },
    directives:{
        'b-modal': VBModal,
        Ripple
    },
    computed: {
        currentLocale() {
            return  this.$i18n.locale;
        },
    },
    methods: {
        async confirmRemoveBuilding(item, index) {
            try {
                const confirmed = await this.$bvModal.msgBoxConfirm(
                    'Please confirm that you want to delete this building?', 
                    {
                        title: 'Please Confirm',
                        size: 'sm',
                        okVariant: 'danger',
                        okTitle: 'Yes',
                        cancelTitle: 'No',
                        cancelVariant: 'outline-secondary',
                        hideHeaderClose: false,
                        centered: true,
                    }
                );

                if (confirmed) {
                    await this.$store.dispatch('app-house-pin/deleteBuilding', {
                        id: item.id,
                    });

                    this.buildings.splice(index, 1);

                    this.$toast({
                        component: ToastificationContent,
                        props: {
                            title: 'Deleted!',
                            icon: 'CheckIcon',
                            variant: 'success',
                            text: 'The building has been successfully deleted.',
                        },
                    });
                }
            } catch (error) {
                this.$toast({
                    component: ToastificationContent,
                        props: {
                        title: 'Error',
                        icon: 'AlertTriangleIcon',
                        variant: 'danger',
                        text: 'There was a problem deleting the building.',
                    },
                });
            }
        },
        async deleteFloorplan(item) {
            try {
                const confirmed = await this.$bvModal.msgBoxConfirm(
                    `Please confirm that you want to delete ${item.title[this.currentLocale]}?`, 
                    {
                        title: 'Please Confirm',
                        size: 'sm',
                        okVariant: 'danger',
                        okTitle: 'Yes',
                        cancelTitle: 'No',
                        cancelVariant: 'outline-secondary',
                        hideHeaderClose: false,
                        centered: true,
                    }
                );

                if (confirmed) {
                    await this.$store.dispatch('app-house-pin/deleteFloorplan', item.id);

                    this.refetchData()

                    this.$toast({
                        component: ToastificationContent,
                        props: {
                            title: 'Deleted!',
                            icon: 'CheckIcon',
                            variant: 'success',
                            text: 'The floor plan has been successfully deleted.',
                        },
                    });
                }
            } catch (error) {
                this.$toast({
                    component: ToastificationContent,
                        props: {
                        title: 'Error',
                        icon: 'AlertTriangleIcon',
                        variant: 'danger',
                        text: 'There was a problem deleting the floor plan.',
                    },
                });
            }
        },
        async updateFloorplanOrdering(evt, item) {
            await store.dispatch('app-house-pin/orderingFloorplan', item)
        },
        async changeOrderBuilding() {
            const ordered = this.buildings.map((b, index) => ({
                id: b.id,
                ordering_count: index + 1
            }));
            await store.dispatch('app-house-pin/orderingBuilding', ordered);
            await this.refetchData();
        },
    },
    setup(){
        const LANGUAGES_STORE_MODULE_NAME = 'app-languages';
        if (!store.hasModule(LANGUAGES_STORE_MODULE_NAME)) store.registerModule(LANGUAGES_STORE_MODULE_NAME, languagesStoreModule)

        const HOUSE_PIN_STORE_MODULE_NAME = 'app-house-pin';
        if (!store.hasModule(HOUSE_PIN_STORE_MODULE_NAME)) store.registerModule(HOUSE_PIN_STORE_MODULE_NAME, housePinStoreModule)

        onUnmounted(() => {
            if (store.hasModule(LANGUAGES_STORE_MODULE_NAME)) store.unregisterModule(LANGUAGES_STORE_MODULE_NAME)
            if (store.hasModule(HOUSE_PIN_STORE_MODULE_NAME)) store.unregisterModule(HOUSE_PIN_STORE_MODULE_NAME)
        });

        const {
            buildings,
            addBuilding,
            isBuildingFormSidebarActive,
            buildingsData,
            refetchData,
            langs,
            housePin,
            updateBuilding,
            getHousePinDetail,
            addFloorPlan,
            buildingItem,
            isFloorplanFormSidebarActive,
        } = useHousePinFloorPlan();

        return {
            buildings,
            addBuilding,
            isBuildingFormSidebarActive,
            buildingsData,
            refetchData,
            langs,
            housePin,
            updateBuilding,
            getHousePinDetail,
            addFloorPlan,
            buildingItem,
            isFloorplanFormSidebarActive,
        }
    }
}
</script>

<style>
    .button-edit {
    margin-right: 16px;
    }
</style>